/**
 * AI mode in mock mode (screens, tests) — works on the same mock chat db as ../mock.ts:
 *   c150  the AI is in charge mid-sale (LINE test account) — side card shows what it understood
 *   c151  a fresh hand-off "ลูกค้าถามส่วนลด" (FB test page) — banner + warning chip
 *   c101  a test account where the AI may take over (button enabled)
 *   c406 comment · everything else "not a test account" (button disabled with the reason)
 */
import { ApiError } from '../client'
import type { MockChatApi } from '../mock'
import { STAFF } from '../mockData'
import type { ChatMessage, ConversationDetail, ConversationSummary, SystemEvent } from '../types'
import { toSummary } from '../../lib/inbox'
import type { AiModeApi, AiModeEvent, AiModeView, AiSession, AiTurn } from './types'

const clone = <T>(v: T): T => structuredClone(v)
export const MOCK_TEST_CHATS = new Set(['c150', 'c151', 'c101'])

type Listener = (e: AiModeEvent) => void
const listeners = new Set<Listener>()
export function onMockAiMode(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
export function emitMockAiMode(e: AiModeEvent): void {
  for (const fn of listeners) fn(clone(e))
}

export class MockAiModeApi implements AiModeApi {
  enabled = true
  private sessions = new Map<string, AiSession>()
  private turns = new Map<string, AiTurn[]>()

  constructor(private readonly chat: MockChatApi) {
    const now = chat.db.now
    const iso = (secAgo: number) => new Date(now - secAgo * 1000).toISOString()
    const c150 = chat.db.conversations.get('c150')
    if (c150?.ai_mode) {
      this.sessions.set('c150', {
        id: c150.ai_mode.session_id, open: true, by: c150.ai_mode.by, started_at: c150.ai_mode.since, ended_at: null,
        end_reason: null, handoff: null, order_id: null, turns: 3, errors: 0,
        state: {
          stage: 'collect_address',
          package: 'แพ็ก 7 วัน 2 มื้อ/วัน',
          items: [
            { name: 'อกไก่ย่างเทริยากิ', qty: 4 }, { name: 'แซลมอนย่างซีอิ๊วญี่ปุ่น', qty: 4 },
            { name: 'ปลาดอลลี่นึ่งมะนาว', qty: 3 }, { name: 'สเต๊กหมูพริกไทยดำ', qty: 3 },
          ],
          menu_code: 'A7K2Q',
          address: { area: 'บางนา', zip: null },
          customer: null,
          quote: null,
          slip: null,
        },
      })
      this.turns.set('c150', [
        {
          id: 't3', at: iso(7 * 60), trigger_message_id: 'c150-m5', output: {}, error: null, ms: 2140, model: 'gemini-flash',
          cost_usd: 0.0021,
          tools: [{ name: 'parse_menu_code', ok: true }, { name: 'catalog', ok: true }],
          debug: { understood: 'ลูกค้าส่งรหัสเมนู A7K2Q ครบ 14 มื้อ ขั้นต่อไปขอชื่อและที่อยู่' },
        },
        {
          id: 't2', at: iso(20 * 60), trigger_message_id: 'c150-m3', output: {}, error: null, ms: 1830, model: 'gemini-flash',
          cost_usd: 0.0018, tools: [{ name: 'ship_area', ok: true }], debug: null,
        },
      ])
    }
    const c151 = chat.db.conversations.get('c151')
    if (c151?.ai_handoff) {
      this.sessions.set('c151', {
        id: 's151', open: false, by: { id: STAFF.jit.id, name: STAFF.jit.name }, started_at: iso(15 * 60),
        ended_at: c151.ai_handoff.at, end_reason: 'handoff',
        handoff: { code: 'discount', note: 'ลูกค้าถามส่วนลด', at: c151.ai_handoff.at }, order_id: null, turns: 2, errors: 0,
        state: { stage: 'choose_package', package: 'แพ็ก 14 วัน' },
      })
    }
  }

  private conv(id: string): ConversationDetail {
    const c = this.chat.db.conversations.get(id)
    if (!c) throw new ApiError(404, 'ไม่พบห้องแชทนี้')
    return c
  }

  private reason(c: ConversationDetail): string | null {
    if (c.kind === 'comment') return 'คอมเมนต์ใต้โพสต์ให้ AI ดูแลไม่ได้ ตอบในหน้าคอมเมนต์'
    if (!this.enabled) return 'เจ้าของยังไม่เปิดโหมด AI ดูแลแชท (ตั้งค่า › AI)'
    if (c.kind !== 'dm') return 'AI ดูแลได้เฉพาะแชทส่วนตัว (ไม่ใช่กลุ่ม)'
    if (!MOCK_TEST_CHATS.has(c.id)) return 'ตอนนี้ AI ดูแลได้เฉพาะบัญชีทดสอบ (เปิด "เฉพาะบัญชีทดสอบ" อยู่)'
    if (c.status === 'closed') return 'แชทนี้ปิดอยู่ เปิดแชทอีกครั้งก่อน'
    if (c.ai_mode) return 'AI ดูแลแชทนี้อยู่แล้ว'
    return null
  }

  async view(convId: string): Promise<AiModeView> {
    const c = this.conv(convId)
    const why = this.reason(c)
    return clone({
      conversation_id: convId,
      enabled: this.enabled,
      can_turn_on: { ok: why === null, reason: why },
      session: this.sessions.get(convId) ?? null,
      recent_turns: this.turns.get(convId) ?? [],
    })
  }

  private line(c: ConversationDetail, event: 'ai_on' | 'ai_off', reason: string | null): void {
    const me = this.chat.db.team.find((m) => m.id === this.chat.db.meId)
    const ev: SystemEvent = {
      kind: 'system', id: `e${++this.chat.db.seq}`, conversation_id: c.id, at: new Date().toISOString(), event,
      staff: me ? { id: me.id, name: me.name } : null, reason,
    }
    this.chat.db.threads.get(c.id)?.push(ev)
    this.chat.publish({ type: 'message.created', conversation_id: c.id, item: ev, conversation: toSummary(c) })
  }

  async set(convId: string, on: boolean): Promise<ConversationSummary> {
    const c = this.conv(convId)
    const me = this.chat.db.team.find((m) => m.id === this.chat.db.meId) ?? { id: 'ploy', name: 'พลอย' }
    if (on) {
      const why = this.reason(c)
      if (why) throw new ApiError(why.includes('อยู่แล้ว') || why.includes('ปิด') ? 409 : 422, why)
      const since = new Date().toISOString()
      const sid = `s${++this.chat.db.seq}`
      c.ai_mode = { session_id: sid, by: { id: me.id, name: me.name }, since, handoff: null }
      c.ai_handoff = null
      this.sessions.set(convId, {
        id: sid, open: true, by: { id: me.id, name: me.name }, started_at: since, ended_at: null, end_reason: null,
        handoff: null, order_id: null, turns: 0, errors: 0, state: {},
      })
      this.turns.set(convId, [])
      this.line(c, 'ai_on', null)
    } else if (c.ai_mode) {
      c.ai_mode = null
      const s = this.sessions.get(convId)
      if (s) Object.assign(s, { open: false, ended_at: new Date().toISOString(), end_reason: 'turned_off' })
      this.line(c, 'ai_off', 'turned_off')
    }
    const conversation = toSummary(c)
    this.chat.publish({ type: 'conversation.updated', conversation })
    return clone(conversation)
  }

  // ------------------------------------------------ "AI กำลังคิด → กำลังพิมพ์ → ฟอง" (screens + dev demo)
  private timers: ReturnType<typeof setTimeout>[] = []

  private addMessage(c: ConversationDetail, msg: ChatMessage): void {
    this.chat.db.threads.get(c.id)?.push(msg)
    const at = msg.at
    c.last_message = { text: msg.text ?? '', type: 'text', direction: msg.direction, at }
    c.last_message_at = at
    if (msg.direction === 'in') {
      c.waiting_since = c.needs_reply && c.waiting_since ? c.waiting_since : at
      c.needs_reply = true
    } else {
      c.needs_reply = false
      c.waiting_since = null
    }
    this.chat.publish({ type: 'message.created', conversation_id: c.id, item: msg, conversation: toSummary(c) })
  }

  /**
   * The customer writes, the AI thinks, then "types" and sends — like api/app/ai/aimode_runner.py (WS ai.busy).
   * Resolves when the bubble has landed. Mock only.
   */
  simulateTurn(
    convId: string,
    opts: { customer?: string; reply?: string; thinkMs?: number; typeMs?: number } = {},
  ): Promise<void> {
    const c = this.conv(convId)
    const customer = opts.customer ?? 'เอาตามนี้ค่ะ'
    const reply = opts.reply ?? 'รับทราบค่ะ สรุปคำสั่งซื้อ แพ็ก 7 วัน 2 มื้อ/วัน ครบ 14 มื้อค่ะ รบกวนขอชื่อและที่อยู่จัดส่งด้วยนะคะ'
    const thinkMs = opts.thinkMs ?? 4000
    const typeMs = opts.typeMs ?? 5000
    const sid = c.ai_mode?.session_id ?? 's-mock'
    const trigger = String(++this.chat.db.seq)
    const busy = (state: 'thinking' | 'typing' | 'idle', extra: { seconds?: number; left?: number } = {}) =>
      emitMockAiMode({ type: 'ai.busy', conversation_id: convId, session_id: sid, trigger_id: trigger, state, at: Date.now(), ttl: 180, ...extra })
    const now = () => new Date().toISOString()
    if (customer) {
      this.addMessage(c, {
        kind: 'message', id: `m${trigger}`, conversation_id: convId, direction: 'in', type: 'text', text: customer,
        attachments: [], at: now(), author: { kind: 'contact' }, read: false,
      })
    }
    busy('thinking')
    return new Promise((resolve) => {
      this.timers.push(
        setTimeout(() => {
          busy('typing', { seconds: Math.round(typeMs / 100) / 10, left: 1 })
          this.timers.push(
            setTimeout(() => {
              this.addMessage(c, {
                kind: 'message', id: `m${++this.chat.db.seq}`, conversation_id: convId, direction: 'out', type: 'text',
                text: reply, attachments: [], at: now(), author: { kind: 'ai', topic: null }, read: false,
                send: { status: 'sent', error: null, client_id: `ai-${trigger}` },
              })
              busy('idle')
              resolve()
            }, typeMs),
          )
        }, thinkMs),
      )
    })
  }

  /**
   * The customer writes and the AI hands the chat back instead of answering (api/app/ai/aimode.py end_session
   * 'handoff'): nothing is sent, the chat leaves the AI tab for กำลังคุย › ต้องตอบ with the warning chip, and the
   * person in charge gets the alert. Mock only (tests, screens).
   */
  simulateHandoff(convId: string, opts: { customer?: string | null; code?: string; note?: string } = {}): void {
    const c = this.conv(convId)
    if (!c.ai_mode) return
    const sid = c.ai_mode.session_id
    const code = opts.code ?? 'discount'
    const note = opts.note ?? 'ลูกค้าถามส่วนลด'
    const at = () => new Date(this.chat.db.now).toISOString()
    const customer = opts.customer === undefined ? 'ถ้าสั่ง 2 แพ็กพร้อมกัน มีส่วนลดไหมคะ' : opts.customer
    if (customer) {
      this.addMessage(c, {
        kind: 'message', id: `m${++this.chat.db.seq}`, conversation_id: convId, direction: 'in', type: 'text',
        text: customer, attachments: [], at: at(), author: { kind: 'contact' }, read: false,
      })
    }
    c.ai_mode = null
    c.ai_handoff = { code, note, at: at() }
    const s = this.sessions.get(convId)
    if (s) Object.assign(s, { open: false, ended_at: at(), end_reason: 'handoff', handoff: { code, note, at: at() } })
    this.chat.publish({ type: 'conversation.updated', conversation: toSummary(c) })
    emitMockAiMode({
      type: 'ai.handoff', conversation_id: convId, session_id: sid, staff_id: c.assigned_staff?.id ?? null, code, note,
    })
  }

  /** Dev mock only: c150 (AI in charge) goes through a turn now and then, so the row can be seen live. */
  startDemo(everyMs = 60000): void {
    const loop = () => {
      this.timers.push(
        setTimeout(() => {
          if (this.chat.db.conversations.get('c150')?.ai_mode) void this.simulateTurn('c150').then(loop)
          else loop()
        }, everyMs),
      )
    }
    loop()
  }

  stop(): void {
    for (const t of this.timers) clearTimeout(t)
    this.timers = []
  }

  async acceptHandoff(convId: string): Promise<ConversationSummary> {
    const c = this.conv(convId)
    const me = this.chat.db.team.find((m) => m.id === this.chat.db.meId)
    c.ai_handoff = null
    if (me && c.assigned_staff?.id !== me.id) c.assigned_staff = { id: me.id, name: me.name }
    const conversation = toSummary(c)
    this.chat.publish({ type: 'conversation.updated', conversation })
    return clone(conversation)
  }
}
